"use client";

/**
 * /start — the front door.
 *
 * Gordon 2026-08-06: rather than splitting the app onto a subdomain
 * (app./open.), breakinbread.com/start is the welcome + info page and /home is
 * the app. One domain, one deploy, no DNS or auth-domain juggling, and the
 * marketing story still gets its own page.
 *
 * "Open the app" goes to /home. Native platform buttons are deliberately
 * rendered but DISABLED until each store build actually ships, so the page
 * states the roadmap honestly instead of promising a download that 404s.
 * Installing the PWA works today, so that button is live.
 */

import Link from "next/link";
import { useEffect, useState } from "react";
import {
  ArrowRight, Apple, Smartphone, Download, Check, Loader2,
  Music2, Store, Radio, Clapperboard, Users, Coins,
  CalendarDays, Building2, HeartHandshake,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useBranding } from "@/contexts/branding-context";
import { usePWAInstall } from "@/hooks/use-pwa-install";
import { cn } from "@/lib/utils";
import { BBShowreelMosaic } from "@/components/start/bb-showreel-mosaic";

/**
 * Native builds. `available` flips to true only when that store listing is
 * live, until then the button renders greyed with its real status, which is
 * the whole point of showing it.
 */
const PLATFORMS: {
  id: string;
  label: string;
  icon: React.ElementType;
  available: boolean;
  status: string;
  href?: string;
}[] = [
  { id: "ios", label: "iOS", icon: Apple, available: false, status: "In development", href: "/ios" },
  { id: "android", label: "Android", icon: Smartphone, available: false, status: "In development", href: "/android" },
  { id: "harmony", label: "HarmonyOS", icon: Smartphone, available: false, status: "Planned" },
];

const FEATURES = [
  { icon: Music2, title: "Release your music", body: "Upload, master, and publish, with splits and credits handled properly from the start." },
  { icon: Coins, title: "Get paid fairly", body: "Transparent cuts, PWYW downloads, and a payouts backbone built for artists, not against them." },
  { icon: Clapperboard, title: "Creative studios", body: "Artwork, film, podcast and lyric-video tools built into the platform, no extra subscriptions." },
  { icon: Store, title: "Marketplace", body: "Sell merch, physical media, services and sync licences directly to the people who listen." },
  { icon: Radio, title: "Radio & events", body: "Run stations, host listening parties, and sell tickets to live and virtual shows." },
  { icon: Users, title: "A real community", body: "Artists, labels, producers and fans in one place, with profiles, walls, and collaboration built in." },
];

interface Category {
  id: string;
  label: string;
  icon: React.ElementType;
  href: string;
  blurb: string;
  detail: string;
}

const CATEGORIES: Category[] = [
  { id: "releases", label: "Releases", icon: Music2, href: "/releases",
    blurb: "Upload, master, and publish your music.",
    detail: "Release singles, EPs, albums and mixtapes with splits and credits handled properly from the start. Cover art, contributors, ISRC/ISWC and lyrics all live on the release itself." },
  { id: "events", label: "Events", icon: CalendarDays, href: "/events",
    blurb: "Host and sell tickets to live and virtual shows.",
    detail: "Run listening parties, gigs and livestreams. Sell tickets, manage guest lists, and let fans discover what's happening near them or online." },
  { id: "studios", label: "Studios", icon: Clapperboard, href: "/artwork-studio",
    blurb: "Artwork, film, podcast and AI creative tools.",
    detail: "Design cover art, edit video, record multi-participant podcasts, and generate lyric videos and AI scenes, all built into the platform so you're not paying for five separate subscriptions." },
  { id: "venues", label: "Venues", icon: Building2, href: "/community",
    blurb: "Connect with venues booking independent artists.",
    detail: "Venue profiles show capacity, gear and booking contact, so artists can find rooms to play and venues can find acts to fill them." },
  { id: "crowdsourcing", label: "Crowdsourcing", icon: HeartHandshake, href: "/collab",
    blurb: "Get help finishing tracks, for a share of the rights.",
    detail: "Post a demo that needs a mix, a verse, or mastering. Collaborators trade their time for a percentage of the rights instead of an upfront fee." },
  { id: "marketplace", label: "Marketplace", icon: Store, href: "/marketplace",
    blurb: "Sell merch, physical media, services and licences.",
    detail: "List merch, vinyl and physical media, session and mixing services, and sync licences directly to the people who already listen to you." },
  { id: "radio", label: "Radio", icon: Radio, href: "/radio",
    blurb: "Run your own station or tune into others.",
    detail: "Launch a branded station, schedule mixtapes and DJ sets, and reach listeners actively looking for new independent music." },
  { id: "royalties", label: "Royalties", icon: Coins, href: "/earnings",
    blurb: "Transparent cuts and fast payouts.",
    detail: "See exactly what you earn from streams, downloads and sales, with clear platform cuts and payouts to your own bank or card." },
];

export default function StartPage() {
  const { logoUrl, appName } = useBranding();
  const { canInstall, isIos, isInstalled, install } = usePWAInstall();
  const [installing, setInstalling] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [activeCategory, setActiveCategory] = useState<Category | null>(null);

  // Install state is client-only; render the same markup on the server and
  // decide after mount so hydration stays clean.
  useEffect(() => { setMounted(true); }, []);

  const handleInstall = async () => {
    setInstalling(true);
    try { await install(); } finally { setInstalling(false); }
  };

  return (
    <div className="min-h-[calc(100dvh_-_var(--header-height))] w-full">
      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden border-b border-border/60">
        <BBShowreelMosaic className="absolute inset-0 opacity-25 sm:opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/85 to-background" />
        <div className="relative mx-auto max-w-4xl px-page py-16 sm:py-24 text-center">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={logoUrl}
            alt={appName}
            className="mx-auto h-20 sm:h-24 w-auto max-w-full object-contain drop-shadow-2xl"
          />
          <h1 className="mt-6 text-4xl sm:text-6xl font-black tracking-tight leading-[1.05]">
            {appName}
          </h1>
          <p className="mt-4 text-xl sm:text-2xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            The independent music platform where artists keep control:
          </p>

          <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2.5 max-w-2xl mx-auto">
            {CATEGORIES.map((c) => {
              const Icon = c.icon;
              return (
                <button
                  key={c.id}
                  onClick={() => setActiveCategory(c)}
                  className="flex items-center justify-center gap-1.5 rounded-full border border-border bg-card/90 backdrop-blur px-3 py-2.5 text-sm sm:text-base font-medium leading-tight text-center hover:border-primary hover:bg-card transition-colors"
                >
                  <Icon className="h-4 w-4 text-primary shrink-0" />
                  <span>{c.label}</span>
                </button>
              );
            })}
          </div>
          <p className="mt-3 text-base text-muted-foreground">All in one place. Tap any to learn more.</p>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button asChild size="lg" className="gap-2 text-base h-12 px-8 w-full sm:w-auto">
              <Link href="/home">
                Open the app <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="text-base h-12 px-8 w-full sm:w-auto">
              <Link href="/signin">Create an account</Link>
            </Button>
          </div>

          <p className="mt-4 text-sm text-muted-foreground">
            Works in any browser. No download needed.
          </p>
        </div>
      </section>

      {/* Category detail popover */}
      <Dialog open={!!activeCategory} onOpenChange={(o) => !o && setActiveCategory(null)}>
        <DialogContent className="max-w-md">
          {activeCategory && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <activeCategory.icon className="h-5 w-5 text-primary" />
                  {activeCategory.label}
                </DialogTitle>
                <DialogDescription>{activeCategory.blurb}</DialogDescription>
              </DialogHeader>
              <p className="text-sm text-muted-foreground leading-relaxed">{activeCategory.detail}</p>
              <Button asChild className="w-full mt-2 gap-2">
                <Link href={activeCategory.href}>
                  Explore {activeCategory.label} <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Get the app ───────────────────────────────────────────────────── */}
      <section className="mx-auto max-w-4xl px-page py-14">
        <h2 className="text-3xl sm:text-4xl font-bold text-center">Get the app</h2>
        <p className="mt-2 text-center text-base text-muted-foreground">
          Native apps are on the way. Install the web app today. It lives on
          your home screen, launches faster, and opens full-screen.
        </p>

        {/* Install-as-app: genuinely available right now, so it leads. */}
        <div className="mt-7 flex justify-center">
          {mounted && isInstalled ? (
            <div className="inline-flex items-center gap-2 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-5 py-3 text-sm font-medium">
              <Check className="h-4 w-4 text-emerald-500" /> Installed, you&apos;re all set
            </div>
          ) : mounted && canInstall ? (
            <Button size="lg" className="gap-2 h-12 px-8" onClick={handleInstall} disabled={installing}>
              {installing
                ? <><Loader2 className="h-4 w-4 animate-spin" /> Installing…</>
                : <><Download className="h-4 w-4" /> Install the web app</>}
            </Button>
          ) : mounted && isIos ? (
            <div className="rounded-xl border border-border bg-card px-5 py-4 text-sm text-center max-w-sm">
              <p className="font-medium">Install on iPhone or iPad</p>
              <p className="mt-1 text-muted-foreground text-xs leading-relaxed">
                Tap the Share button in Safari, then <strong>Add to Home Screen</strong>.
              </p>
            </div>
          ) : (
            <Button asChild size="lg" className="gap-2 h-12 px-8">
              <Link href="/home"><Download className="h-4 w-4" /> Open in browser</Link>
            </Button>
          )}
        </div>

        {/* Native stores, shown but disabled until each build actually ships. */}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {PLATFORMS.map(p => {
            const Icon = p.icon;
            const button = (
              <div
                className={cn(
                  "flex items-center gap-3 rounded-xl border px-4 py-3.5 transition-colors",
                  p.available
                    ? "border-border bg-card hover:border-primary cursor-pointer"
                    : "border-border/50 bg-muted/30 opacity-55 cursor-not-allowed",
                )}
              >
                <Icon className={cn("h-6 w-6 shrink-0", !p.available && "text-muted-foreground")} />
                <div className="min-w-0 text-left">
                  <p className="text-sm font-semibold leading-tight">{p.label}</p>
                  <p className="text-[11px] text-muted-foreground leading-tight">
                    {p.available ? "Download" : p.status}
                  </p>
                </div>
                {!p.available && (
                  <span className="ml-auto rounded-full bg-muted px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-muted-foreground shrink-0">
                    Soon
                  </span>
                )}
              </div>
            );
            // A store link opens in a new tab; a coming-soon platform with its own
            // page (/ios, /android) opens that page — install steps live there.
            return p.available && p.href
              ? <a key={p.id} href={p.href} target="_blank" rel="noopener noreferrer">{button}</a>
              : p.href
                ? <a key={p.id} href={p.href} title={`${p.label}: ${p.status}`} className="[&>div]:cursor-pointer [&>div]:opacity-80 hover:[&>div]:opacity-100">{button}</a>
                : <div key={p.id} aria-disabled="true" title={`${p.label}: ${p.status}`}>{button}</div>;
          })}
        </div>
      </section>

      {/* ── What it is ────────────────────────────────────────────────────── */}
      <section className="border-t border-border/60 bg-muted/20">
        <div className="mx-auto max-w-5xl px-page py-14">
          <h2 className="text-3xl sm:text-4xl font-bold text-center">
            Everything an independent artist needs
          </h2>
          <div className="mt-9 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map(f => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="rounded-xl border border-border bg-card p-5">
                  <Icon className="h-5 w-5 text-primary" />
                  <h3 className="mt-3 font-semibold text-lg">{f.title}</h3>
                  <p className="mt-1.5 text-base text-muted-foreground leading-relaxed">{f.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Closing CTA ───────────────────────────────────────────────────── */}
      <section className="mx-auto max-w-3xl px-page py-16 text-center">
        <h2 className="text-3xl sm:text-4xl font-bold">Ready when you are</h2>
        <p className="mt-3 text-base text-muted-foreground">
          Free to join. Bring your catalogue, or start your first release today.
        </p>
        <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Button asChild size="lg" className="gap-2 h-12 px-8 w-full sm:w-auto">
            <Link href="/home">Open the app <ArrowRight className="h-4 w-4" /></Link>
          </Button>
          <Button asChild variant="outline" size="lg" className="h-12 px-8 w-full sm:w-auto">
            <Link href="/help">How it works</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
